import { Injectable } from '@angular/core';
import { Observable, Subject, of } from 'rxjs';
import { takeUntil, map, catchError } from 'rxjs/operators';
import * as _ from 'lodash';
import { RdoHttpService } from '../core/http/rdo-http.service';
import { FormatService } from '../core/query/format.service';
import { GeneralExtendedQuery } from '../models/body';
import { ErrorService } from '../core/errors/error.service';

@Injectable()
export class SearchService {
  private productTypeCancel$ = new Subject<void>();
  private customersCancel$ = new Subject<void>();
  private salesRepsCancel$ = new Subject<void>();
  private assetsCancel$ = new Subject<void>();

  constructor(
    private rdoHttp: RdoHttpService,
    private formatService: FormatService,
    private errorService: ErrorService
  ) {}

  public searchProductTypes = (query: string): Observable<any> => {
    this.productTypeCancel$.next();
    return this.search(query, 'equipment').pipe(
      takeUntil(this.productTypeCancel$)
    );
  };

  public searchCustomers = (query: string): Observable<any> => {
    this.customersCancel$.next();
    return this.search(query, 'customers').pipe(
      takeUntil(this.customersCancel$)
    );
  };

  public searchSalesReps = (query: string): Observable<any> => {
    this.salesRepsCancel$.next();
    return this.search(query, 'sales-reps').pipe(
      takeUntil(this.salesRepsCancel$)
    );
  };

  public searchAssets = (query: string): Observable<any> => {
    this.assetsCancel$.next();
    return this.search(query, 'assets').pipe(takeUntil(this.assetsCancel$));
  };

  private mapProperties = (r: object) => {
    return _.map(r as any, (i: any) => {
      i.ClientBenchmarkedRevenueDisplay = this.formatService.formatCurrency(
        i.ClientBenchmarkedRevenue,
        false
      );
      const difference = Math.round(
        i.ClientBenchmarkedRevenue - i.BenchmarkedRevenue
      );
      const differencePercent = Math.round(
        (i.BenchmarkedRevenue !== 0 ? difference / i.BenchmarkedRevenue : 0) *
          100
      );
      i.BenchmarkedRevenueDifferenceDisplay = `${this.formatService.formatCurrency(difference, false)} / ${differencePercent} %`;
      i.IsBenchmarkedDifferenceBelow =
        i.ClientBenchmarkedRevenue < i.BenchmarkedRevenue;
      return i;
    });
  };

  private search = (query: string, type: string): Observable<any> => {
    let cancelSubject$;
    if (type === 'equipment') {
      cancelSubject$ = this.productTypeCancel$;
    } else if (type === 'customers') {
      cancelSubject$ = this.customersCancel$;
    } else if (type === 'sales-reps') {
      cancelSubject$ = this.salesRepsCancel$;
    } else {
      cancelSubject$ = this.assetsCancel$;
    }
    const pagedSorted = new GeneralExtendedQuery();
    pagedSorted.q = query;
    pagedSorted.PageSize = 5;
    pagedSorted.OrderBy = 'RevenueBenchmarked';
    pagedSorted.OrderByDirection = 'DESC';

    if (type === 'equipment') {
      pagedSorted.IncludeTotal = false;
      pagedSorted.IncludeGrowth = false;
    }
    return this.rdoHttp
      .setCancelSubject(cancelSubject$)
      .post(`search/${type}`, pagedSorted)
      .pipe(
        map((value: any) => this.mapProperties(value)),
        catchError((err) => {
          const message = err
            ? err.message
              ? err.message
              : err.toString()
            : 'Error is null';
          this.errorService.logSentryError(
            err,
            `header.service:search[${type}] failed: ${message}`
          );
          return of([]); // Or: throwError(() => err)
        })
      );
  };
}
